import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import {
  useGetGuidParam,
  useGetOptionalGuidParam,
} from '@risksmart-app/components/src/routes/routes.utils';
import {
  Approval_Status_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { Permission } from 'src/rbac/Permission';

import { useDeleteAcceptances } from '@/hooks/mutations/acceptance';
import { useGetAcceptanceById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';
import { getFriendlyId } from '@/utils/friendlyId';
import {
  acceptanceDetailUrl,
  acceptanceRegisterUrl,
  riskDetailsUrl,
} from '@/utils/urls';

import PageLayout from '../../../../layouts/PageLayout';

const Page = () => {
  const parentId = useGetOptionalGuidParam('riskId');
  const acceptanceId = useGetGuidParam('acceptanceId');
  const navigate = useNavigate();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const { deleteAcceptances, loading: deleteLoading } = useDeleteAcceptances();
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // Present only on the details tab when object_toolbar + form_sections are on.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);

  const tabs = useTabs({
    parentType: Parent_Type_Enum.Acceptance,
    parent: null,
    hrefRoot: acceptanceDetailUrl(acceptanceId),
  });
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], { keyPrefix: 'acceptances' });
  const { data } = useGetAcceptanceById({
    queryArgs: { acceptanceId },
  });
  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      if (!acceptanceId) {
        return false;
      }
      await deleteAcceptances([acceptanceId]);
      setIsDeleted(true);
      if (parentId) {
        navigate(riskDetailsUrl(parentId));
      } else {
        navigate(acceptanceRegisterUrl());
      }

      return true;
    },
  });
  const fallbackTitle = st('fallback_title');

  const acceptance = data?.acceptance[0];
  // An acceptance whose deletion is already awaiting approval cannot take a
  // second delete request — the approval gate rejects it. The details tab
  // already SHOWS this (FormContext's DeleteRequestAlert), but the header's
  // Delete stayed live, so the only thing stopping a pointless rejected request
  // was the user reading the banner (COI-916).
  //
  // `.some()` over the raw set: the read deliberately neither orders nor
  // de-duplicates the relation, because this question does not need it.
  const hasPendingDeleteRequest = (acceptance?.changeRequests ?? []).some(
    (changeRequest) =>
      changeRequest.ChangeRequestStatus === Approval_Status_Enum.Pending &&
      changeRequest.Type === 'delete'
  );
  const counter =
    acceptance &&
    `(${getFriendlyId(Parent_Type_Enum.Acceptance, acceptance?.SequentialId)})`;

  // Object-level toolbar (CUS-158) — flag-gated. Relocates Delete into the
  // toolbar's overflow ⋮ menu and surfaces the Save disc / Cancel X (save state
  // bridged from the active tab's form via useObjectToolbarFormStore). The
  // legacy detail page has no export action, so the toolbar surfaces none.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title: acceptance?.Title ?? fallbackTitle,
        // Pass the already-parenthesised counter (the toolbar renders it verbatim).
        counter: counter || undefined,
      },
      parentObject: acceptance,
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('edit_form'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('edit_tabs'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        danger: [
          {
            id: 'delete',
            label: st('delete_button'),
            iconName: 'trash-01',
            permission: 'delete:acceptance',
            // Hydration gate (ENG-616) — parity with risks/assets — plus the
            // in-flight deletion gate (COI-916).
            disabled: !acceptance || hasPendingDeleteRequest,
            onClick: () => setIsDeleteModalVisible(true),
          },
        ],
      },
    }),
    [
      acceptance,
      hasPendingDeleteRequest,
      fallbackTitle,
      counter,
      t,
      st,
      formConfig,
    ]
  );

  if (isDeleted) {
    return <></>;
  }
  if (data?.acceptance.length === 0) {
    throw new PageNotFound(`Acceptance with id ${acceptanceId} not found`);
  }

  return (
    <PageLayout
      helpTranslationKey={'acceptances.help'}
      title={acceptance?.Title}
      meta={{ title: fallbackTitle }}
      counter={counter}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Permission
            permission={'delete:acceptance'}
            parentObject={acceptance}
          >
            <Button
              variant={'normal'}
              formAction={'none'}
              disabled={hasPendingDeleteRequest}
              onClick={() => {
                setIsDeleteModalVisible(true);
              }}
            >
              {st('delete_button')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <ControlledTabs
        tabs={tabs}
        variant={'container'}
        parentType={Parent_Type_Enum.Acceptance}
        parent={acceptance}
        disableSettings={objectToolbarEnabled}
      />
      <TabSettingsModal
        parentType={Parent_Type_Enum.Acceptance}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={acceptance}
      />
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={st('delete_modal_title')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_single_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
